<template>
  <vx-card>
    <div class="flex flex-wrap justify-between items-center mb-6">
      <div>
        <h3>Edit Pengaturan Laporan</h3>
        <p class="text-sm opacity-75">{{ company.name || 'Company' }} / Tahun {{ selectedYear }}</p>
      </div>
      <vs-button to="/report-settings" type="border">Kembali</vs-button>
    </div>

    <div v-if="loading" class="py-6">Memuat akun dan pengaturan laporan...</div>
    <template v-else-if="loaded">
      <p v-if="categorizedCount === 0" class="mb-4 text-warning text-sm">
        COA tahun ini belum memiliki kategori tersimpan. Klasifikasikan dahulu di
        <router-link to="/manage-coa">Manage COA</router-link>, atau pilih akun dari "Semua kategori".
      </p>
      <vs-alert v-if="isIncomeStatement" :color="unmappedIncomeAccounts.length ? 'warning' : 'success'" class="mb-5">
        <strong>{{ unmappedIncomeAccounts.length ? `${unmappedIncomeAccounts.length} akun perlu dipetakan` : 'Seluruh akun Laba Rugi sudah dipetakan' }}</strong>
        <p class="mt-2">Setiap akun Laba Rugi harus masuk tepat satu bagian. Pengaturan parsial dapat disimpan, tetapi laporan belum dapat diterbitkan.</p>
        <label class="block mt-3"><input v-model="unmappedOnly" type="checkbox" /> Tampilkan akun yang belum dipetakan</label>
      </vs-alert>
      <div class="mb-5">
        <label class="block mb-2">Nama laporan</label>
        <vs-input v-model="reportName" class="w-full" placeholder="Nama pengaturan laporan" />
      </div>

      <div class="report-scope-note mb-4">
        <strong>Kategori akun dan susunan laporan punya fungsi berbeda.</strong>
        <p class="mt-2">Kategori dan saldo normal diatur di <router-link to="/manage-coa">Manage COA</router-link>. Di sini, pilih akun yang masuk laporan<span v-if="isIncomeStatement"> dan tentukan bagiannya. Akun berkategori Beban perlu dibagi menjadi Beban Penjualan atau Administrasi &amp; Umum</span>.</p>
        <p class="mt-2">Mengganti filter tidak menghapus pilihan yang sudah ada.</p>
      </div>
      <div v-if="isBalanceSheet" class="report-scope-note mb-4">
        <strong>Aset = Liabilitas + Ekuitas</strong>
        <p class="mt-2">Isi otomatis akun posting dari kategori COA pada company dan tahun aktif. Pilihan yang sudah ada tetap dipertahankan.</p>
        <p class="mt-2">{{ balanceSheetCategorySummary }}</p>
        <vs-button class="mt-3" :disabled="saving || !unselectedBalanceSheetAccounts.length" @click="selectBalanceSheetAccounts">
          Pilih otomatis akun Neraca ({{ unselectedBalanceSheetAccounts.length }})
        </vs-button>
        <p v-if="!balanceSheetAccounts.length" class="mt-2">Belum ada akun posting dengan kategori Aset, Liabilitas, atau Ekuitas dan jenis laporan Neraca. Lengkapi di <router-link to="/manage-coa">Manage COA</router-link>.</p>
        <p class="mt-2">Akun yang belum memiliki kategori atau jenis laporan Neraca tidak dipilih otomatis. Periksa pilihan lalu klik Simpan pengaturan laporan. Pemilihan akun tidak mengubah saldo transaksi.</p>
      </div>
      <div class="flex flex-wrap items-end mb-4">
        <div class="mr-4 mb-2">
          <label class="block mb-2">Kategori akun</label>
          <select v-model="categoryFilter" class="p-2 border rounded" style="min-width: 220px">
            <option value="all">Semua kategori ({{ accounts.length }})</option>
            <option v-for="option in categoryOptions" :key="option.value" :value="option.value">
              {{ option.label }} ({{ categoryCount(option.value) }})
            </option>
            <option value="unclassified">Belum diklasifikasikan ({{ categoryCount('unclassified') }})</option>
          </select>
        </div>
        <vs-input v-model="search" class="mr-4 mb-2" placeholder="Cari nomor atau nama akun" />
        <label class="mb-3 text-sm"><input v-model="selectedOnly" type="checkbox" /> Tampilkan yang dipilih saja</label>
      </div>

      <div class="flex flex-wrap report-account-panels">
        <div class="report-account-list pr-4">
          <div class="flex justify-between mb-2 text-sm">
            <span>{{ filteredAccounts.length }} akun ditampilkan</span>
            <span>{{ selectedIds.length }} akun dipilih</span>
          </div>
          <div class="overflow-auto border rounded" style="max-height: 58vh">
            <table class="w-full report-account-table">
              <thead><tr><th>Pilih</th><th>No akun</th><th>Nama akun</th><th>Level</th><th>Kategori</th><th v-if="isIncomeStatement">Bagian Laba Rugi</th></tr></thead>
              <tbody>
                <tr v-for="account in filteredAccounts" :key="account.id" :class="{ 'selected-account-row': isSelected(account.id) }">
                  <td><input type="checkbox" :checked="isSelected(account.id)" :aria-label="`Pilih ${account.account_code} ${account.name}`" @change="toggleAccount(account.id, $event.target.checked)" /></td>
                  <td>{{ account.account_code }}</td>
                  <td>{{ account.name }}</td>
                  <td>{{ account.level }}</td>
                  <td>{{ categoryLabel(account.account_category) }}</td>
                  <td v-if="isIncomeStatement">
                    <select :value="sectionByAccount[account.id] || ''" class="section-select" @change="setAccountSection(account.id, $event.target.value)">
                      <option value="">Pilih section</option>
                      <option v-for="section in sectionOptionsFor(account)" :key="section.code" :value="section.code">{{ section.label }}</option>
                    </select>
                  </td>
                </tr>
                <tr v-if="!filteredAccounts.length"><td :colspan="isIncomeStatement ? 6 : 5" class="text-center py-4">Tidak ada akun untuk filter ini.</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="report-selected-list pl-4">
          <h4 class="mb-2">Akun dalam laporan ({{ selectedIds.length }})</h4>
          <div class="overflow-auto border rounded" style="max-height: 58vh">
            <table class="w-full report-account-table">
              <tbody>
                <tr v-for="account in selectedAccounts" :key="account.id">
                  <td>{{ account.account_code }} - {{ account.name }}<small v-if="isIncomeStatement" class="block opacity-75">{{ sectionLabel(sectionByAccount[account.id]) }}</small></td>
                  <td><button type="button" class="text-danger" :aria-label="`Hapus ${account.name}`" @click="toggleAccount(account.id, false)">Hapus</button></td>
                </tr>
                <tr v-if="!selectedAccounts.length"><td class="py-4 text-center">Belum ada akun dipilih.</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="divider my-5"></div>
      <vs-button :disabled="saving || !canSave" @click="submitData">{{ saving ? 'Menyimpan...' : 'Simpan pengaturan laporan' }}</vs-button>
      <vs-button to="/report-settings" class="ml-3" type="border" color="danger">Batal</vs-button>
    </template>
  </vx-card>
</template>

<script>
import moduleAccount from '@/store/accounts/moduleAccount.js'

export default {
  data () {
    return {
      reportName: '',
      reportCode: '',
      accounts: [],
      selectedIds: [],
      sectionByAccount: {},
      categoryFilter: 'all',
      search: '',
      selectedOnly: false,
      unmappedOnly: false,
      loading: false,
      loaded: false,
      saving: false,
      loadedYear: null,
      loadedCompanyId: null,
      categoryOptions: [
        { value: 'asset', label: 'Aset' },
        { value: 'liability', label: 'Liabilitas' },
        { value: 'equity', label: 'Ekuitas' },
        { value: 'income', label: 'Pendapatan' },
        { value: 'expense', label: 'Beban' },
        { value: 'cost_of_sales', label: 'Harga Pokok Penjualan' },
        { value: 'other_income', label: 'Pendapatan Lain-lain' },
        { value: 'other_expense', label: 'Beban Lain-lain' }
      ],
      sectionOptions: [
        { code: 'REVENUE', label: 'Pendapatan', category: 'income' },
        { code: 'COST_OF_SALES', label: 'Harga Pokok Penjualan', category: 'cost_of_sales' },
        { code: 'SELLING_EXPENSE', label: 'Beban Penjualan', category: 'expense' },
        { code: 'ADMIN_EXPENSE', label: 'Beban Administrasi & Umum', category: 'expense' },
        { code: 'OTHER_INCOME', label: 'Pendapatan Lain-lain', category: 'other_income' },
        { code: 'OTHER_EXPENSE', label: 'Beban Lain-lain', category: 'other_expense' }
      ]
    }
  },
  computed: {
    company () {
      return JSON.parse(localStorage.getItem('activeCompany') || 'null') || {}
    },
    selectedYear () {
      return localStorage.getItem('selectedYear') || '-'
    },
    selectedAccounts () {
      const selected = new Set(this.selectedIds)
      return this.accounts.filter(account => selected.has(account.id))
    },
    categorizedCount () {
      return this.accounts.filter(account => !!account.account_category).length
    },
    unmappedIncomeAccounts () {
      return this.accounts.filter(account => account.account_kind === 'POSTING' &&
        (account.statement_type === 'income_statement' || account.closing_behavior === 'close_to_equity' || this.sectionOptions.some(section => section.category === account.account_category)) &&
        (!this.selectedIds.includes(account.id) || !this.sectionByAccount[account.id]))
    },
    filteredAccounts () {
      const query = this.search.trim().toLowerCase()
      const selected = new Set(this.selectedIds)
      return this.accounts.filter(account => {
        if (this.isIncomeStatement && (account.account_kind !== 'POSTING' || !(account.statement_type === 'income_statement' || account.closing_behavior === 'close_to_equity' || this.sectionOptions.some(section => section.category === account.account_category)))) return false
        if (this.isIncomeStatement && this.unmappedOnly && !this.unmappedIncomeAccounts.some(item => item.id === account.id)) return false
        if (this.categoryFilter === 'unclassified' && account.account_category) return false
        if (this.categoryFilter !== 'all' && this.categoryFilter !== 'unclassified' && account.account_category !== this.categoryFilter) return false
        if (this.selectedOnly && !this.unmappedOnly && !selected.has(account.id)) return false
        return !query || `${account.account_code} ${account.name}`.toLowerCase().includes(query)
      })
    },
    canSave () {
      if (this.reportName.trim().length === 0 || this.selectedIds.length === 0) return false
      return !this.isIncomeStatement || this.selectedIds.every(id => !!this.sectionByAccount[id])
    },
    isIncomeStatement () {
      return this.reportCode === 'INCOME_STATEMENT'
    },
    isBalanceSheet () {
      return this.reportCode === 'BALANCE_SHEET'
    },
    balanceSheetAccounts () {
      return this.accounts.filter(account => account.account_kind === 'POSTING' &&
        ['asset', 'liability', 'equity'].includes(account.account_category) &&
        account.statement_type === 'balance_sheet')
    },
    unselectedBalanceSheetAccounts () {
      const selected = new Set(this.selectedIds)
      return this.balanceSheetAccounts.filter(account => !selected.has(account.id))
    },
    balanceSheetCategorySummary () {
      return ['asset', 'liability', 'equity'].map(category => {
        const count = this.balanceSheetAccounts.filter(account => account.account_category === category).length
        return `${this.categoryLabel(category)}: ${count} akun`
      }).join(' · ')
    }
  },
  watch: {
    '$route.params.id' () {
      if (this.company.id) this.load()
    }
  },
  created () {
    if (!moduleAccount.isRegistered) {
      this.$store.registerModule('accounts', moduleAccount)
      moduleAccount.isRegistered = true
    }
    if (this.company.id) this.load()
  },
  methods: {
    selectBalanceSheetAccounts () {
      if (!this.isBalanceSheet || this.saving || !this.loaded) return
      this.selectedIds = [...new Set([...this.selectedIds, ...this.balanceSheetAccounts.map(account => account.id)])]
      this.categoryFilter = 'all'
      this.search = ''
      this.selectedOnly = true
    },
    load () {
      const yearAtLoad = this.selectedYear
      const companyIdAtLoad = Number(this.company.id)
      this.loading = true
      this.loaded = false
      Promise.all([
        this.$store.dispatch('accounts/fetchAccounts', { company_id: companyIdAtLoad }),
        this.$store.dispatch('reportSettings/showReportSetting', {
          company_id: companyIdAtLoad,
          report_id: this.$route.params.id
        })
      ]).then(([accountsResponse, reportResponse]) => {
        if (this.selectedYear !== yearAtLoad || Number(this.company.id) !== companyIdAtLoad) {
          throw new Error('Tahun atau company aktif berubah. Muat ulang halaman edit laporan.')
        }
        const report = reportResponse.data.data
        this.reportCode = report.report_code || ''
        this.accounts = (accountsResponse.data.data || []).map(account => ({
          ...account,
          id: Number(account.id)
        }))
        const accountIds = new Set(this.accounts.map(account => account.id))
        this.sectionByAccount = {}
        if (this.reportCode === 'INCOME_STATEMENT') {
          const sections = report.section_configuration || []
          sections.forEach(section => {
            const sectionAccounts = section.accounts || []
            sectionAccounts.forEach(account => {
              const id = Number(account.id)
              if (accountIds.has(id)) this.$set(this.sectionByAccount, id, section.section_code)
            })
          })
          this.selectedIds = Object.keys(this.sectionByAccount).map(Number)
        } else {
          this.selectedIds = [...new Set((report.accounts || []).map(account => Number(account.id)))]
            .filter(id => accountIds.has(id))
        }
        this.reportName = report.name || ''
        this.loadedYear = yearAtLoad
        this.loadedCompanyId = companyIdAtLoad
        this.loaded = true
      }).catch(error => this.notifyError(error))
        .finally(() => { this.loading = false })
    },
    categoryCount (category) {
      return this.accounts.filter(account => {
        if (category === 'unclassified') return !account.account_category
        return account.account_category === category
      }).length
    },
    categoryLabel (category) {
      const option = this.categoryOptions.find(item => item.value === category)
      return option ? option.label : 'Belum diklasifikasikan'
    },
    isSelected (id) {
      return this.selectedIds.includes(id)
    },
    toggleAccount (id, checked) {
      const accountId = Number(id)
      if (checked && !this.selectedIds.includes(accountId)) {
        this.selectedIds.push(accountId)
        if (this.isIncomeStatement && !this.sectionByAccount[accountId]) {
          const account = this.accounts.find(item => item.id === accountId)
          const options = this.sectionOptionsFor(account)
          const section = options.length === 1 ? options[0] : null
          if (section) this.$set(this.sectionByAccount, accountId, section.code)
        }
      }
      if (!checked) {
        this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== accountId)
        this.$delete(this.sectionByAccount, accountId)
      }
    },
    setAccountSection (id, sectionCode) {
      const accountId = Number(id)
      if (!sectionCode) {
        this.$delete(this.sectionByAccount, accountId)
        this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== accountId)
        return
      }
      this.$set(this.sectionByAccount, accountId, sectionCode)
      if (!this.selectedIds.includes(accountId)) this.selectedIds.push(accountId)
    },
    sectionOptionsFor (account) {
      if (!account) return []
      return this.sectionOptions.filter(section => section.category === account.account_category)
    },
    sectionLabel (code) {
      const section = this.sectionOptions.find(item => item.code === code)
      return section ? section.label : 'Section belum dipilih'
    },
    sectionAssignmentsPayload () {
      const assignments = {}
      this.sectionOptions.forEach(section => { assignments[section.code] = [] })
      this.selectedIds.forEach(id => {
        const section = this.sectionByAccount[id]
        if (section && assignments[section]) assignments[section].push(id)
      })
      return assignments
    },
    submitData () {
      if (!this.canSave || this.saving) return
      if (this.selectedYear !== this.loadedYear || Number(this.company.id) !== this.loadedCompanyId) {
        this.notifyError(new Error('Tahun atau company aktif berubah. Muat ulang halaman sebelum menyimpan.'))
        return
      }
      this.saving = true
      this.$store.dispatch('reportSettings/updateReportSettings', {
        name: this.reportName.trim(),
        company_id: this.company.id,
        account_id: this.selectedIds,
        report_id: this.$route.params.id,
        section_assignments: this.isIncomeStatement ? this.sectionAssignmentsPayload() : undefined
      }).then(() => {
        this.$vs.notify({ color: 'success', title: 'Berhasil', text: 'Pengaturan laporan berhasil diperbarui.' })
        this.$router.push('/report-settings')
      }).catch(error => this.notifyError(error))
        .finally(() => { this.saving = false })
    },
    notifyError (error) {
      const response = error && error.response && error.response.data
      const errors = response && response.errors
      const firstError = errors && Object.keys(errors).length ? errors[Object.keys(errors)[0]][0] : null
      this.$vs.notify({ color: 'danger', title: 'Gagal', text: firstError || (response && response.message) || (error && error.message) || 'Pengaturan laporan tidak dapat dimuat.' })
    }
  }
}
</script>

<style scoped>
.report-scope-note { padding: 16px; border: 1px solid var(--app-primary-soft); border-radius: 8px; background: var(--app-primary-soft); color: #31415a; font-size: 13px; line-height: 1.6; }
.report-account-list { width: 65%; }
.report-selected-list { width: 35%; }
.report-account-table th, .report-account-table td { padding: 9px 11px; border-bottom: 1px solid rgba(128, 128, 128, .2); text-align: left; }
.report-account-table th { white-space: nowrap; }
.selected-account-row { background: rgba(var(--vs-primary), .08); }
.section-select { min-width: 190px; padding: 7px; border: 1px solid rgba(128, 128, 128, .35); border-radius: 5px; background: transparent; color: inherit; }
.divider { background: #e0e0e0; height: 1px; }
@media (max-width: 960px) {
  .report-account-list, .report-selected-list { width: 100%; padding: 0; }
  .report-selected-list { margin-top: 20px; }
}
</style>
